import { registerOverlay } from "klinecharts";
import { generatePivots, PivotPatternType } from "../utils/pivotDetector";
export function ensureStepOverlayRegistered(vm) {
  if (vm.stepRegistered) return;
  try {
    registerOverlay({
      name: "KITE_STEP",
      createPointFigures: ({ coordinates, overlay }) => {
        const coords = coordinates || [];
        const pts = overlay?.points || [];
        if (coords.length < 2) return [];
        const color = overlay?.styles?.line?.color ?? "#0c86e9ff";
        const size = overlay?.styles?.line?.size ?? 2;
        const pivotHigh = overlay?.styles?.pivot?.highColor ?? "#2962FF";
        const pivotLow = overlay?.styles?.pivot?.lowColor ?? "#9a3225";
        const pivotR = overlay?.styles?.pivot?.radius ?? 4;
        const clipTop = overlay?.styles?.pivot?.clipTop ?? 10;
        const figs = [];
        for (let i = 0; i < coords.length - 1; i++) {
          const a = coords[i];
          const b = coords[i + 1];
          if (!a || !b) continue;
          figs.push({
            type: "line",
            attrs: { coordinates: [{ x: a.x, y: a.y }, { x: b.x, y: a.y }] },
            styles: { color, size, style: "solid" },
          });
          figs.push({
            type: "line",
            attrs: { coordinates: [{ x: b.x, y: a.y }, { x: b.x, y: b.y }] },
            styles: { color, size, style: "solid" },
          });
        }
        for (let i = 0; i < coords.length; i++) {
          const c = coords[i];
          const p = pts[i];
          if (!c || !p) continue;
          const py = Number(p.pivotY);
          if (!Number.isFinite(py)) continue;
          if (py <= clipTop) continue;
          const isHigh = !!p.isHigh;
          const pc = isHigh ? pivotHigh : pivotLow;
          figs.push({
            type: "circle",
            attrs: { x: c.x, y: py, r: pivotR },
            styles: { color: pc, fillColor: pc },
          });
        }
        return figs;
      },
    });
    vm.stepRegistered = true;
  } catch (e) {
    vm.stepRegistered = true;
  }
}
export function isLineMode(vm) {
  return vm.chartType?.key === "line";
}
export function isStepMode(vm) {
  return vm.chartType?.key === "step";
}
export function isAreaMode(vm) {
  return vm.chartType?.key === "area";
}
export function removeStepOverlay(vm) {
  if (!vm.stepOverlayId) return;
  try { vm.chart?.removeOverlay?.(vm.stepOverlayId); } catch (e) { }
  vm.stepOverlayId = null;
}
function resetAreaToVisible(vm) {
  vm.chart?.setStyles?.({
    candle: {
      area: {
        lineColor: "#000",
        lineSize: 1,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
    },
  });
}
export function enableAreaMode(vm) {
  if (!vm.chart) return;
  removeStepOverlay(vm);
  vm.chart.setStyles?.({
    candle: {
      type: "area",
      area: {
        lineColor: "#0c86e9ff",
        lineSize: 2,
        backgroundColor: "#438ae1d2",
        fillColor: "#438ae1d2",
      },
    },
  });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
  vm.chart.resize?.();
}
export function enableLineMode(vm) {
  if (!vm.chart) return;
  removeStepOverlay(vm);
  vm.chart.setStyles?.({
    candle: {
      type: "area",
      area: {
        lineColor: "#000",
        lineSize: 2,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
    },
  });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
  vm.chart.resize?.();
}
export function renderStepOverlay(vm) {
  if (!vm.chart || !Array.isArray(vm.bars) || !vm.bars.length) return;
  ensureStepOverlayRegistered(vm);
  removeStepOverlay(vm);
  const paneId = vm.mainPaneId || "candle_pane";
  const { pivots } = generatePivots(vm.bars);
  const pivByTs = new Map(pivots.map(p => [Number(p.timestamp), p]));
  const toPx = (pt) => {
    let px = null;
    try { px = vm.chart.convertToPixel?.(pt, paneId); } catch (_) { }
    if (!px) { try { px = vm.chart.convertToPixel?.(pt, { paneId }); } catch (_) { } }
    return px;
  };
  const pts = vm.bars.map((b) => {
    const pivot = pivByTs.get(Number(b.timestamp));
    let pivotY = null;
    let isHigh = null;
    if (pivot) {
      const px = toPx({ timestamp: b.timestamp, value: Number(pivot.price) });
      pivotY = px?.y;
      isHigh = pivot.type === PivotPatternType.HIGH;
    }
    return {
      timestamp: b.timestamp,
      value: Number(b.close),
      pivotY,
      isHigh,
    };
  });
  const id = vm.chart.createOverlay?.({
    name: "KITE_STEP",
    paneId,
    lock: true,
    points: pts,
    zLevel: 1,
    styles: {
      line: { color: "#0c86e9ff", size: 2 },
      pivot: { radius: 4, highColor: "#2962FF", lowColor: "#9a3225" }
    },
  }) ?? null;
  if (id == null) {
    requestAnimationFrame(() => renderStepOverlay(vm));
    return;
  }
  vm.stepOverlayId = id;
}
export function enableStepMode(vm) {
  if (!vm.chart) return;
  removeStepOverlay(vm);
  vm.chart.setStyles?.({
    candle: {
      type: "area",
      area: {
        lineColor: "transparent",
        lineSize: 0,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
    },
  });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
  vm.chart.resize?.();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      renderStepOverlay(vm);
    });
  });
}
export function disableStepMode(vm) {
  removeStepOverlay(vm);
  resetAreaToVisible(vm);
}
export function applyChartType(vm, { nativeTypes, chartColors } = {}) {
  if (!vm.chart) return;
  const key = vm.chartType?.key;
  if (key !== "step") disableStepMode(vm);
  if (key !== "colored_hlc_bar") {
    removeHlcOverlay(vm);
  }
  if (key !== "scatter") {
    removeScatterOverlay(vm);
  }
  if (key !== "histogram") {
    removeHistogramOverlay(vm);
  }
  if (!key) {
    resetAreaToVisible(vm);
    resetToCandleDefaults(vm, chartColors);
    vm.chart.setStyles?.({ candle: { type: "candle_solid" } });
    vm.applyNoPriceLabelStyles?.();
    vm.chart.refresh?.();
    return;
  }
  if (key === "area") {
    enableAreaMode(vm);
    return;
  }
  if (key === "line") {
    enableLineMode(vm);
    return;
  }
  if (key === "step") {
    enableStepMode(vm);
    return;
  }
  if (key === "colored_hlc_bar") {
    enableColoredHlcBarMode(vm);
    return;
  }
  if (key === "scatter") {
    enableScatterMode(vm);
    return;
  }
  if (key === "histogram") {
    enableHistogramMode(vm);
    return;
  }
  if (key === "bar") {
    resetAreaToVisible(vm);
    vm.chart.setStyles?.({
      candle: {
        type: "ohlc",
        bar: {
          upColor: chartColors?.mono ?? "#444",
          downColor: chartColors?.mono ?? "#444",
          noChangeColor: chartColors?.mono ?? "#444",
        },
      },
    });
    vm.applyNoPriceLabelStyles?.();
    vm.chart.refresh?.();
    return;
  }
  if (key === "colored_bar") {
    resetAreaToVisible(vm);
    vm.chart.setStyles?.({
      candle: {
        type: "ohlc",
        bar: {
          upColor: chartColors?.up ?? "#26a69a",
          downColor: chartColors?.down ?? "#ef5350",
          noChangeColor: chartColors?.neutral ?? "#999",
        },
      },
    });
    vm.applyNoPriceLabelStyles?.();
    vm.chart.refresh?.();
    return;
  }
  const nt = nativeTypes ?? new Set();
  if (nt.has(key)) {
    resetAreaToVisible(vm);
    resetToCandleDefaults(vm, chartColors);
    vm.chart.setStyles?.({ candle: { type: key } });
    vm.applyNoPriceLabelStyles?.();
    vm.chart.refresh?.();
    return;
  }
  resetAreaToVisible(vm);
  resetToCandleDefaults(vm, chartColors);
  vm.chart.setStyles?.({ candle: { type: "candle_solid" } });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
}
export function ensureHlcOverlayRegistered(vm) {
  if (vm.hlcRegistered) return;
  try {
    registerOverlay({
      name: "KITE_HLC_BAR",
      createPointFigures: ({ coordinates, overlay }) => {
        const coords = coordinates || [];
        const pts = overlay?.points || [];
        if (!coords.length || !pts.length) return [];
        const size = overlay?.styles?.line?.size ?? 2;
        const tick = overlay?.styles?.line?.tick ?? 7;
        const rule = overlay?.extendData?.colorRule ?? "prevClose";
        const upColor = overlay?.styles?.line?.upColor ?? "#26a69a";
        const downColor = overlay?.styles?.line?.downColor ?? "#ef5350";
        const neutralColor = overlay?.styles?.line?.neutralColor ?? "#666";
        const pivotHigh = overlay?.styles?.pivot?.highColor ?? "#2962FF";
        const pivotLow = overlay?.styles?.pivot?.lowColor ?? "#9a3225";
        const pivotR = overlay?.styles?.pivot?.radius ?? 4;
        const clipTop = overlay?.styles?.pivot?.clipTop ?? 10;
        const figs = [];
        for (let i = 0; i < coords.length; i++) {
          const c = coords[i];
          const p = pts[i];
          if (!c || !p) continue;
          const close = Number(p.value);
          if (!Number.isFinite(close)) continue;
          let color = neutralColor;
          if (rule === "open") {
            const open = Number(p.open);
            if (Number.isFinite(open)) color = close >= open ? upColor : downColor;
          } else {
            const prevClose = Number(pts[i - 1]?.value);
            if (Number.isFinite(prevClose)) {
              color = close > prevClose ? upColor : close < prevClose ? downColor : neutralColor;
            } else {
              const open = Number(p.open);
              if (Number.isFinite(open)) color = close >= open ? upColor : downColor;
            }
          }
          const highY = Number(p.highY);
          const lowY = Number(p.lowY);
          const closeY = c.y;
          if (Number.isFinite(highY) && Number.isFinite(lowY)) {
            figs.push({
              type: "line",
              attrs: { coordinates: [{ x: c.x, y: highY }, { x: c.x, y: lowY }] },
              styles: { color, size, style: "solid" },
            });
          }
          figs.push({
            type: "line",
            attrs: { coordinates: [{ x: c.x, y: closeY }, { x: c.x + tick, y: closeY }] },
            styles: { color, size, style: "solid" },
          });
          const py = Number(p.pivotY);
          if (Number.isFinite(py) && py > clipTop) {
            const isHigh = !!p.isHigh;
            const pc = isHigh ? pivotHigh : pivotLow;
            figs.push({
              type: "circle",
              attrs: { x: c.x, y: py, r: pivotR },
              styles: { color: pc, fillColor: pc },
            });
          }
        }
        return figs;
      },
    });
    vm.hlcRegistered = true;
  } catch (e) {
    vm.hlcRegistered = true;
  }
}
export function removeHlcOverlay(vm) {
  if (!vm.hlcOverlayId) return;
  try { vm.chart?.removeOverlay?.(vm.hlcOverlayId); } catch (_) { }
  vm.hlcOverlayId = null;
}
export function renderHlcOverlay(vm) {
  if (!vm.chart || !Array.isArray(vm.bars) || !vm.bars.length) return;
  ensureHlcOverlayRegistered(vm);
  removeHlcOverlay(vm);
  const paneId = vm.mainPaneId || "candle_pane";
  const { pivots } = generatePivots(vm.bars);
  const pivByTs = new Map(pivots.map(p => [Number(p.timestamp), p]));
  const toPx = (pt) => {
    let px = null;
    try { px = vm.chart.convertToPixel?.(pt, paneId); } catch (_) { }
    if (!px) { try { px = vm.chart.convertToPixel?.(pt, { paneId }); } catch (_) { } }
    return px;
  };
  const pts = vm.bars.map((b) => {
    const highPx = toPx({ timestamp: b.timestamp, value: Number(b.high) });
    const lowPx = toPx({ timestamp: b.timestamp, value: Number(b.low) });
    const pivot = pivByTs.get(Number(b.timestamp));
    let pivotY = null;
    let isHigh = null;
    if (pivot) {
      const px = toPx({ timestamp: b.timestamp, value: Number(pivot.price) });
      pivotY = px?.y;
      isHigh = pivot.type === PivotPatternType.HIGH;
    }
    return {
      timestamp: b.timestamp,
      value: Number(b.close),
      open: Number(b.open),
      highY: highPx?.y,
      lowY: lowPx?.y,
      pivotY,
      isHigh,
    };
  });
  const id = vm.chart.createOverlay?.({
    name: "KITE_HLC_BAR",
    paneId,
    lock: true,
    points: pts,
    zLevel: 1,
    extendData: { colorRule: "prevClose" },
    styles: {
      line: { size: 2, tick: 7, upColor: "#26a69a", downColor: "#ef5350", neutralColor: "#666" },
      pivot: { radius: 4, highColor: "#2962FF", lowColor: "#9a3225" },
    },
  }) ?? null;
  if (id == null) {
    requestAnimationFrame(() => renderHlcOverlay(vm));
    return;
  }
  vm.hlcOverlayId = id;
}
export function enableColoredHlcBarMode(vm) {
  if (!vm.chart) return;
  removeStepOverlay?.(vm);
  removeHlcOverlay(vm);
  vm.chart.setStyles?.({
    candle: {
      type: "area",
      area: {
        lineColor: "transparent",
        lineSize: 0,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
    },
  });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
  vm.chart.resize?.();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      renderHlcOverlay(vm);
    });
  });
}
export function ensureScatterOverlayRegistered(vm) {
  if (vm.scatterRegistered) return;
  try {
    registerOverlay({
      name: "KITE_SCATTER",
      createPointFigures: ({ coordinates, overlay }) => {
        const coords = coordinates || [];
        const pts = overlay?.points || [];
        if (!coords.length || !pts.length) return [];
        const size = overlay?.styles?.point?.size ?? 4;
        const rule = overlay?.extendData?.colorRule ?? "mono";
        const mono = overlay?.styles?.point?.color ?? "#333";
        const upColor = overlay?.styles?.point?.upColor ?? "#26a69a";
        const downColor = overlay?.styles?.point?.downColor ?? "#ef5350";
        const neutralColor = overlay?.styles?.point?.neutralColor ?? "#777";
        const pivotHigh = overlay?.styles?.pivot?.highColor ?? "#2962FF";
        const pivotLow = overlay?.styles?.pivot?.lowColor ?? "#9a3225";
        const pivotR = overlay?.styles?.pivot?.radius ?? 4;
        const clipTop = overlay?.styles?.pivot?.clipTop ?? 10;
        const figs = [];
        for (let i = 0; i < coords.length; i++) {
          const c = coords[i];
          const p = pts[i];
          if (!c || !p) continue;
          const close = Number(p.value);
          if (!Number.isFinite(close)) continue;
          let color = mono;
          if (rule !== "mono") {
            if (rule === "open") {
              const open = Number(p.open);
              color = Number.isFinite(open) ? (close >= open ? upColor : downColor) : mono;
            } else {
              const prevClose = Number(pts[i - 1]?.value);
              if (!Number.isFinite(prevClose)) color = neutralColor;
              else color = close >= prevClose ? upColor : downColor;
            }
          }
          figs.push({
            type: "rect",
            attrs: { x: c.x - size / 2, y: c.y - size / 2, width: size, height: size },
            styles: { color, fillColor: color },
          });
          const py = Number(p.pivotY);
          if (Number.isFinite(py) && py > clipTop) {
            const pc = p.isHigh ? pivotHigh : pivotLow;
            figs.push({
              type: "circle",
              attrs: { x: c.x, y: py, r: pivotR },
              styles: { color: pc, fillColor: pc },
            });
          }
        }
        return figs;
      },
    });
    vm.scatterRegistered = true;
  } catch (e) {
    vm.scatterRegistered = true;
  }
}
export function removeScatterOverlay(vm) {
  if (!vm.scatterOverlayId) return;
  try { vm.chart?.removeOverlay?.(vm.scatterOverlayId); } catch (_) { }
  vm.scatterOverlayId = null;
}
export function renderScatterOverlay(vm) {
  if (!vm.chart || !Array.isArray(vm.bars) || !vm.bars.length) return;
  ensureScatterOverlayRegistered(vm);
  removeScatterOverlay(vm);
  const paneId = vm.mainPaneId || "candle_pane";
  const { pivots } = generatePivots(vm.bars);
  const pivByTs = new Map(pivots.map(p => [Number(p.timestamp), p]));
  const toPx = (pt) => {
    let px = null;
    try { px = vm.chart.convertToPixel?.(pt, paneId); } catch (_) { }
    if (!px) { try { px = vm.chart.convertToPixel?.(pt, { paneId }); } catch (_) { } }
    return px;
  };
  const pts = vm.bars.map((b) => {
    const pivot = pivByTs.get(Number(b.timestamp));
    let pivotY = null;
    let isHigh = null;
    if (pivot) {
      const px = toPx({ timestamp: b.timestamp, value: Number(pivot.price) });
      pivotY = px?.y;
      isHigh = pivot.type === PivotPatternType.HIGH;
    }
    return {
      timestamp: b.timestamp,
      value: Number(b.close),
      open: Number(b.open),
      pivotY,
      isHigh,
    };
  });
  const id = vm.chart.createOverlay?.({
    name: "KITE_SCATTER",
    paneId,
    lock: true,
    points: pts,
    zLevel: 1,
    extendData: { colorRule: "mono" },
    styles: {
      point: { size: 4, color: "#333", upColor: "#26a69a", downColor: "#ef5350", neutralColor: "#777" },
      pivot: { radius: 4, highColor: "#2962FF", lowColor: "#9a3225" },
    },
  }) ?? null;
  if (id == null) {
    requestAnimationFrame(() => renderScatterOverlay(vm));
    return;
  }
  vm.scatterOverlayId = id;
}
export function enableScatterMode(vm) {
  if (!vm.chart) return;
  removeStepOverlay?.(vm);
  removeHlcOverlay?.(vm);
  removeScatterOverlay(vm);
  vm.chart.setStyles?.({
    candle: {
      type: "area",
      area: {
        lineColor: "transparent",
        lineSize: 0,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
    },
  });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
  vm.chart.resize?.();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      renderScatterOverlay(vm);
    });
  });
}
export function ensureHistogramOverlayRegistered(vm) {
  if (vm.histRegistered) return;
  try {
    registerOverlay({
      name: "KITE_HISTOGRAM",
      createPointFigures: ({ coordinates, overlay }) => {
        const coords = coordinates || [];
        const pts = overlay?.points || [];
        if (!coords.length || !pts.length) return [];
        const upColor = overlay?.styles?.bar?.upColor ?? "#26a69a";
        const downColor = overlay?.styles?.bar?.downColor ?? "#ef5350";
        const neutralColor = overlay?.styles?.bar?.neutralColor ?? "#666";
        const opacity = overlay?.styles?.bar?.opacity ?? 0.65;
        const baseY = Number(overlay?.extendData?.baseY);
        if (!Number.isFinite(baseY)) return [];
        const customW = overlay?.styles?.bar?.width;
        let w = Number.isFinite(customW) ? customW : null;
        if (!w) {
          if (coords.length >= 2) {
            const dx = Math.abs(coords[1].x - coords[0].x);
            w = Math.max(2, Math.floor(dx * 0.6));
          } else w = 6;
        }
        const rule = overlay?.extendData?.colorRule ?? "prevClose";
        const pivotHigh = overlay?.styles?.pivot?.highColor ?? "#2962FF";
        const pivotLow = overlay?.styles?.pivot?.lowColor ?? "#9a3225";
        const pivotR = overlay?.styles?.pivot?.radius ?? 4;
        const clipTop = overlay?.styles?.pivot?.clipTop ?? 10;
        const figs = [];
        for (let i = 0; i < coords.length; i++) {
          const c = coords[i];
          const p = pts[i];
          if (!c || !p) continue;
          const close = Number(p.value);
          if (!Number.isFinite(close)) continue;
          let color = neutralColor;
          if (rule === "open") {
            const open = Number(p.open);
            if (Number.isFinite(open)) color = close >= open ? upColor : downColor;
          } else {
            const prevClose = Number(pts[i - 1]?.value);
            if (Number.isFinite(prevClose)) {
              color = close > prevClose ? upColor : close < prevClose ? downColor : neutralColor;
            }
          }
          const y1 = c.y;
          const top = Math.min(y1, baseY);
          const height = Math.max(1, Math.abs(baseY - y1));
          figs.push({
            type: "rect",
            attrs: { x: c.x - w / 2, y: top, width: w, height },
            styles: { color, fillColor: color, opacity },
          });
          const py = Number(p.pivotY);
          if (Number.isFinite(py) && py > clipTop) {
            const pc = p.isHigh ? pivotHigh : pivotLow;
            figs.push({
              type: "circle",
              attrs: { x: c.x, y: py, r: pivotR },
              styles: { color: pc, fillColor: pc },
            });
          }
        }
        return figs;
      },
    });
    vm.histRegistered = true;
  } catch (e) {
    vm.histRegistered = true;
  }
}
export function removeHistogramOverlay(vm) {
  if (!vm.histOverlayId) return;
  try { vm.chart?.removeOverlay?.(vm.histOverlayId); } catch (_) { }
  vm.histOverlayId = null;
}
export function renderHistogramOverlay(vm) {
  if (!vm.chart || !Array.isArray(vm.bars) || !vm.bars.length) return;
  ensureHistogramOverlayRegistered(vm);
  removeHistogramOverlay(vm);
  const paneId = vm.mainPaneId || "candle_pane";
  const windowBars = typeof vm.getVisibleWindowBars === "function" ? vm.getVisibleWindowBars() : vm.bars;
  let baseline = Infinity;
  for (const b of windowBars) baseline = Math.min(baseline, Number(b.low));
  if (!Number.isFinite(baseline)) baseline = Number(vm.bars[0].low);
  const { pivots } = generatePivots(vm.bars);
  const pivByTs = new Map(pivots.map(p => [Number(p.timestamp), p]));
  const toPx = (pt) => {
    let px = null;
    try { px = vm.chart.convertToPixel?.(pt, paneId); } catch (_) { }
    if (!px) { try { px = vm.chart.convertToPixel?.(pt, { paneId }); } catch (_) { } }
    return px;
  };
  const basePx = toPx({ timestamp: windowBars[0].timestamp, value: baseline });
  const baseY = basePx?.y;
  const pts = vm.bars.map((b) => {
    const pivot = pivByTs.get(Number(b.timestamp));
    let pivotY = null;
    let isHigh = null;
    if (pivot) {
      const px = toPx({ timestamp: b.timestamp, value: Number(pivot.price) });
      pivotY = px?.y;
      isHigh = pivot.type === PivotPatternType.HIGH;
    }
    return {
      timestamp: b.timestamp,
      value: Number(b.close),
      open: Number(b.open),
      pivotY,
      isHigh,
    };
  });
  const id = vm.chart.createOverlay?.({
    name: "KITE_HISTOGRAM",
    paneId,
    lock: true,
    points: pts,
    zLevel: 1,
    extendData: { baseY, colorRule: "prevClose" },
    styles: {
      bar: { width: null, opacity: 0.65, upColor: "#26a69a", downColor: "#ef5350", neutralColor: "#666" },
      pivot: { radius: 4, highColor: "#2962FF", lowColor: "#9a3225" },
    },
  }) ?? null;
  if (id == null) {
    requestAnimationFrame(() => renderHistogramOverlay(vm));
    return;
  }
  vm.histOverlayId = id;
}
export function enableHistogramMode(vm) {
  if (!vm.chart) return;
  removeStepOverlay?.(vm);
  removeHlcOverlay?.(vm);
  removeScatterOverlay?.(vm);
  removeHistogramOverlay(vm);
  vm.chart.setStyles?.({
    candle: {
      type: "area",
      area: {
        lineColor: "transparent",
        lineSize: 0,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
    },
  });
  vm.applyNoPriceLabelStyles?.();
  vm.chart.refresh?.();
  vm.chart.resize?.();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      renderHistogramOverlay(vm);
    });
  });
}
function resetToCandleDefaults(vm, chartColors) {
  vm.chart?.setStyles?.({
    candle: {
      area: {
        lineColor: "transparent",
        lineSize: 0,
        backgroundColor: "transparent",
        fillColor: "transparent",
      },
      bar: {
        upColor: chartColors?.up ?? "#26a69a",
        downColor: chartColors?.down ?? "#ef5350",
        noChangeColor: chartColors?.neutral ?? "#999",
      },
      ohlc: {
        upColor: chartColors?.up ?? "#26a69a",
        downColor: chartColors?.down ?? "#ef5350",
        noChangeColor: chartColors?.neutral ?? "#999",
      },
    },
  });
}





